import React from "react";
import { Badge, Card, CellText, EmptyState, Table, Tooltip } from "@opendate-app/setlist";
import { availabilityWindowShape, venueDay, venueTime } from "./availabilityFormatters";

const plural = (count, word) => `${word.toLowerCase()}${count === 1 ? "" : "s"}`;

export const coveragePill = (cell, positionName) => {
  if (!cell) return null;
  const short = cell.needed - cell.fillable;
  if (short <= 0) return { label: `${cell.free}/${cell.needed}`, short: false, title: `${cell.free} free, ${cell.needed} needed` };

  const elsewhere = cell.free - cell.fillable;
  const extra = elsewhere > 0 ? ` (${elsewhere} more ${elsewhere === 1 ? "is" : "are"} free but already counted for another show or position that night)` : "";
  return { label: `${cell.fillable}/${cell.needed}`, short: true, title: `Short ${short}: ${cell.fillable} of ${cell.needed} ${plural(cell.needed, positionName)} can be filled${extra}` };
};

const Dot = ({ color }) => (
  <span aria-hidden="true" style={{ display: "inline-block", width: 8, height: 8, borderRadius: 4, marginRight: 6, background: color || "var(--sl-fg-quaternary)" }} />
);

const AvailabilityByShow = ({ availabilityWindow }) => {
  const { positions, shows } = availabilityWindow;
  const columns = [
    { key: "date", header: "Date", width: 120, render: (show) => <span style={{ whiteSpace: "nowrap" }}>{venueDay(show.date)}</span> },
    {
      key: "show",
      header: "Show",
      primary: true,
      render: (show) => (
        <CellText
          primary={
            <span className="d-inline-flex align-items-center" style={{ gap: 8 }}>
              {show.title}
              {show.hold && (
                <Badge color="gray" size="sm">
                  Hold
                </Badge>
              )}
            </span>
          }
          supporting={[show.room, show.doors_at ? `Doors ${venueTime(show.doors_at)}` : `Starts ${venueTime(show.starts_at)}`].filter(Boolean).join(" · ")}
        />
      ),
    },
    ...positions.map((position) => ({
      key: `position-${position.id}`,
      header: (
        <span className="d-inline-flex align-items-center">
          <Dot color={position.color_hex} />
          {position.name}
        </span>
      ),
      align: "center",
      render: (show) => {
        const pill = coveragePill(
          show.cells.find((cell) => cell.staff_position_id === position.id),
          position.name
        );
        if (!pill) return <span title={`${position.name} not needed`} style={{ color: "var(--sl-text-quaternary)" }}>—</span>;
        return (
          <Tooltip title={pill.title}>
            <Badge color={pill.short ? "error" : "gray"} size="sm">
              {pill.label}
            </Badge>
          </Tooltip>
        );
      },
    })),
    {
      key: "coverage",
      header: "Coverage",
      align: "right",
      render: (show) =>
        show.short > 0 ? (
          <Badge color="error" size="sm">
            Short {show.short}
          </Badge>
        ) : (
          <span style={{ color: "var(--sl-text-tertiary)" }}>Covered</span>
        ),
    },
  ];

  return (
    <>
      <Card padding="none">
        <Table
          columns={columns}
          data={shows}
          getRowKey={(show) => show.calendar_event_id}
          stickyFirstColumn
          empty={<EmptyState size="sm" showRings={false} title="No shows this month" description="Confirmed shows and holds at this venue appear here." />}
        />
      </Card>
      <p style={{ fontSize: 13, lineHeight: "19px", color: "var(--sl-text-tertiary)", marginTop: 12 }}>
        Each pill is people free / people the room’s template needs. Red means the show is short for that position. People are counted once per night, so a red pill
        shows how many slots can actually be filled. Shows on hold are included in case they confirm.
      </p>
    </>
  );
};

AvailabilityByShow.propTypes = { availabilityWindow: availabilityWindowShape.isRequired };

export default AvailabilityByShow;
